import React from 'react';

import { Empty, Progress, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { ActiveTaskType } from 'types/tasks';
import { formatTaskDateTime, formatTaskDuration } from 'utils/tasks';

import {
  SecondaryText,
  StatusBadge,
  TabContent,
  TaskName,
  TaskQueueTabHeader,
  WorkerDetails,
} from './TaskQueueTabContent';

const formatProgress = (task: ActiveTaskType) => {
  if (task.progress_count === null) {
    return <SecondaryText>Not reported</SecondaryText>;
  }

  if (!task.total) {
    return (
      <SecondaryText>
        {task.progress_count.toLocaleString()} {task.unit}
      </SecondaryText>
    );
  }

  const percentage = Math.min(100, Math.round((task.progress_count / task.total) * 100));
  return <Progress percent={percentage} size="small" />;
};

const columns: ColumnsType<ActiveTaskType> = [
  {
    title: 'Task',
    dataIndex: 'name',
    key: 'name',
    width: '30%',
    render: (name, task) => (
      <WorkerDetails>
        <TaskName>{name}</TaskName>
        <SecondaryText>{task.task_id}</SecondaryText>
      </WorkerDetails>
    ),
  },
  {
    title: 'State',
    dataIndex: 'state',
    key: 'state',
    width: '10%',
    render: (state) => <StatusBadge>{state}</StatusBadge>,
  },
  {
    title: 'Worker',
    key: 'worker',
    width: '25%',
    render: (_, task) => (
      <WorkerDetails>
        <span>{task.hostname || 'Unknown host'}</span>
        <SecondaryText>
          {[task.worker, task.pid ? `PID ${task.pid}` : ''].filter(Boolean).join(' · ') || 'Unknown worker'}
        </SecondaryText>
      </WorkerDetails>
    ),
  },
  {
    title: 'Started',
    dataIndex: 'started_at',
    key: 'started_at',
    width: '15%',
    render: formatTaskDateTime,
  },
  {
    title: 'Elapsed',
    dataIndex: 'elapsed_seconds',
    key: 'elapsed_seconds',
    width: '10%',
    render: formatTaskDuration,
  },
  {
    title: 'Progress',
    key: 'progress',
    width: '10%',
    render: (_, task) => formatProgress(task),
  },
];

type Props = {
  tasks: ActiveTaskType[];
  isLoading: boolean;
};

const ActiveTasksTab = ({ tasks, isLoading }: Props) => (
  <TabContent>
    <TaskQueueTabHeader
      title="Active tasks"
      description="Tasks Huey Monitor currently reports as executing. A worker interruption can leave a stale record behind."
    />
    <Table
      rowKey="task_id"
      columns={columns}
      dataSource={tasks}
      loading={isLoading}
      pagination={false}
      locale={{ emptyText: <Empty description="No tasks are currently reported as executing" /> }}
    />
  </TabContent>
);

export default ActiveTasksTab;
